// PresentationBracket — TV-friendly tournament bracket.
//
// Geometry (rounds are columns; each round-r match sits in a slot of 2^(r-1)
// rows) is identical to the admin BracketView and is NOT changed here.
//
// Presentation strategy by bracket size:
//   • ≤16 slots  — single screen, uniformly scaled to fit (may scale up a bit).
//   • 32 slots   — FOUR visual quarters (R1×4 → R2×2 → QF×1 each) rotated by
//                  the shared SlateRotator, plus a fifth "Semifinals & Final"
//                  slate so the top of the draw is still presented.
//   • larger     — falls back to single-screen scaling (reported limitation).
import React from 'react';
import { Check, Trophy } from '@phosphor-icons/react';
import SlateRotator from './SlateRotator';

const FULL = { ROW_H: 120, COL_W: 330, GAP_W: 60, wrap: false, nameFs: 'clamp(12px, 1vw, 15px)', scoreFs: 'clamp(11px, 0.9vw, 13px)' };
const QUARTER = { ROW_H: 205, COL_W: 400, GAP_W: 90, wrap: true, nameFs: '21px', scoreFs: '17px' };

function roundLabel(r, total) {
  if (r === total) return 'Final';
  if (r === total - 1) return 'Semifinals';
  if (r === total - 2) return 'Quarterfinals';
  return `Round ${r}`;
}

function entryName(entry) {
  if (!entry) return null;
  return entry.display_name
    || [entry.player1_name, entry.player2_name].filter(Boolean).join(' / ');
}

function PlayerRow({ entry, isWinner, g }) {
  const wrap = g.wrap;
  const name = entryName(entry);
  if (!name) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[#e3dcd1] text-[#c1bcb1] italic"
        data-testid="pv-bracket-tbd"
        style={{ fontSize: g.nameFs }}
      >
        TBD
      </div>
    );
  }
  return (
    <div
      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${
        isWinner
          ? 'bg-[#f5faf6] border-[#c8dcce] text-[#013523] font-semibold'
          : 'bg-white border-[#ece6dc] text-[#2a332c]'
      }`}
      data-testid={isWinner ? 'pv-bracket-winner-row' : 'pv-bracket-player-row'}
    >
      <span
        className={wrap ? 'leading-snug' : 'truncate'}
        style={wrap
          ? { fontSize: g.nameFs, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }
          : { fontSize: g.nameFs }}
        title={name}
      >
        {name}
      </span>
      {isWinner && (
        <Check size={wrap ? 20 : 14} weight="bold" className="text-[#c9a93a] shrink-0" data-testid="pv-bracket-winner-check" />
      )}
    </div>
  );
}

function MatchCard({ match, g }) {
  const winnerId = match.winner_entry_id;
  const e1Wins = winnerId && match.entry1 && winnerId === match.entry1.id;
  const e2Wins = winnerId && match.entry2 && winnerId === match.entry2.id;
  const isBye = match.score_text === '(bye)';
  return (
    <div
      data-testid={`pv-bracket-match-${match.id}`}
      className="border border-[#dcd4c5] rounded-2xl bg-white p-3 shadow-sm"
    >
      <div className="space-y-1.5">
        <PlayerRow entry={match.entry1} isWinner={!!e1Wins} g={g} />
        <PlayerRow entry={match.entry2} isWinner={!!e2Wins} g={g} />
      </div>
      {match.score_text && (
        <div
          className={`mt-2 pt-2 border-t border-[#f0ebe1] text-center tabular-nums ${
            isBye ? 'italic text-[#9aa39d]' : 'text-[#015e3f] font-semibold'
          }`}
          style={{ fontSize: g.scoreFs }}
          data-testid={`pv-bracket-match-${match.id}-score`}
        >
          {isBye ? 'Bye advance' : match.score_text}
        </div>
      )}
    </div>
  );
}

function ConnectorColumn({ round, slotsInCol, g }) {
  const parents = Math.max(1, Math.round(slotsInCol / (2 ** (round - 1))));
  const halfChildSpan = 2 ** (round - 2);
  return (
    <div className="relative shrink-0" style={{ width: g.GAP_W, height: slotsInCol * g.ROW_H }} aria-hidden>
      {Array.from({ length: parents }).map((_, p) => {
        const childA = 2 * p;
        const childB = 2 * p + 1;
        const cAtop = childA * halfChildSpan * g.ROW_H + (halfChildSpan * g.ROW_H) / 2;
        const cBtop = childB * halfChildSpan * g.ROW_H + (halfChildSpan * g.ROW_H) / 2;
        const parentTop = (cAtop + cBtop) / 2;
        return (
          <React.Fragment key={p}>
            <div className="absolute bg-[#cdbf95]" style={{ left: 0, width: g.GAP_W / 2, top: cAtop - 0.5, height: 1 }} />
            <div className="absolute bg-[#cdbf95]" style={{ left: 0, width: g.GAP_W / 2, top: cBtop - 0.5, height: 1 }} />
            <div className="absolute bg-[#cdbf95]" style={{ left: g.GAP_W / 2 - 0.5, top: cAtop, width: 1, height: cBtop - cAtop }} />
            <div className="absolute bg-[#cdbf95]" style={{ left: g.GAP_W / 2, top: parentTop - 0.5, width: g.GAP_W / 2, height: 1 }} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Renders a (sub-)bracket: `rounds` = [{ round, label, matches }], all
// rounds consecutive; slotsInCol derived from the first round's match count.
function BracketColumns({ rounds, g, footer, aside }) {
  const slotsInCol = rounds[0]?.matches.length || 1;
  const colHeight = slotsInCol * g.ROW_H;
  const totalWidth = rounds.length * g.COL_W + (rounds.length - 1) * g.GAP_W + (aside ? g.COL_W : 0);
  return (
    <div style={{ width: totalWidth }} className="mx-auto">
      <div className="flex" style={{ height: colHeight + 40 }}>
        {rounds.map(({ round, label, matches: rm }, idx) => {
          const slotHeight = (2 ** idx) * g.ROW_H;
          return (
            <React.Fragment key={round}>
              {idx > 0 && (
                <div className="shrink-0 flex flex-col" style={{ width: g.GAP_W }}>
                  <div style={{ height: 40 }} />
                  <ConnectorColumn round={idx + 1} slotsInCol={slotsInCol} g={g} />
                </div>
              )}
              <div className="shrink-0" style={{ width: g.COL_W }} data-testid={`pv-bracket-round-${round}`}>
                <div className="text-center border-b border-[#ece6dc] pb-2 mb-3" style={{ height: 28 }}>
                  <div className="uppercase tracking-[0.22em] font-semibold text-[#013523]" style={{ fontSize: 'clamp(11px, 1vw, 14px)' }}>
                    {label}
                  </div>
                </div>
                <div className="relative" style={{ height: colHeight }}>
                  {rm.map((m, i) => (
                    <div key={m.id} className="absolute left-0 right-0 flex items-center px-2" style={{ top: i * slotHeight, height: slotHeight }}>
                      <div className="w-full"><MatchCard match={m} g={g} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {aside && (
          <div className="shrink-0 flex items-center justify-center" style={{ width: g.COL_W, paddingLeft: g.GAP_W / 2 }}>
            {aside}
          </div>
        )}
      </div>
      {footer}
    </div>
  );
}

function ChampionCard({ tournament }) {
  if (!tournament.champion) return null;
  return (
    <div className="mt-6 mx-auto w-full max-w-md border border-[#c9a93a] rounded-2xl bg-[#fffbf0] px-6 py-4 flex items-center gap-3" data-testid="pv-bracket-champion">
      <Trophy size={26} weight="fill" className="text-[#c9a93a] shrink-0" />
      <div>
        <div className="uppercase tracking-[0.22em] text-[#c9a93a] font-semibold" style={{ fontSize: 'clamp(10px, 0.85vw, 12px)' }}>Champion</div>
        <div className="text-[#013523] font-semibold mt-0.5" style={{ fontSize: 'clamp(16px, 1.4vw, 22px)' }}>{entryName(tournament.champion)}</div>
        {tournament.runner_up && (
          <div className="text-[#6a766e] mt-0.5" style={{ fontSize: 'clamp(11px, 0.9vw, 13px)' }}>
            Runner-up: <span className="text-[#2a332c]">{entryName(tournament.runner_up)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function AdvancesCard({ match }) {
  const winner = match?.winner_entry_id
    ? (match.entry1?.id === match.winner_entry_id ? match.entry1 : match.entry2)
    : null;
  return (
    <div className="w-full border-2 border-[#c9a93a]/70 rounded-2xl bg-[#fffbf0] px-5 py-5 flex flex-col items-center text-center gap-2" data-testid="pv-bracket-advances">
      <Trophy size={30} weight="fill" className="text-[#c9a93a] shrink-0" />
      <div className="uppercase tracking-[0.22em] text-[#c9a93a] font-semibold" style={{ fontSize: 13 }}>Advances to Semifinals</div>
      <div className={winner ? 'text-[#013523] font-semibold' : 'text-[#9aa39d] italic'} style={{ fontSize: 22, lineHeight: 1.2 }}>
        {winner ? entryName(winner) : 'To be decided'}
      </div>
    </div>
  );
}

// Measures the available box and the child's natural size, then applies a
// uniform transform scale (capped at maxScale). Pure presentation wrapper.
function FitToBox({ naturalWidth, maxScale = 1, children, testid }) {
  const boxRef = React.useRef(null);
  const innerRef = React.useRef(null);
  const [scale, setScale] = React.useState(1);
  React.useLayoutEffect(() => {
    const box = boxRef.current; const inner = innerRef.current;
    if (!box || !inner) return undefined;
    const update = () => {
      const bw = box.clientWidth; const bh = box.clientHeight;
      const nh = inner.scrollHeight;
      if (!bw || !bh || !nh) return;
      setScale(Math.min(maxScale, bw / naturalWidth, bh / nh));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(box); ro.observe(inner);
    return () => ro.disconnect();
  }, [naturalWidth, maxScale]);
  return (
    <div ref={boxRef} className="h-full w-full overflow-hidden flex justify-center items-center" data-testid={testid} data-scale={scale.toFixed(3)}>
      <div ref={innerRef} style={{ transform: `scale(${scale})`, transformOrigin: 'center center', width: naturalWidth, flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

function byRound(matches, r) {
  return matches.filter((m) => m.round_number === r).sort((a, b) => a.match_number - b.match_number);
}

export default function PresentationBracket({ tournament, active = true }) {
  const matches = tournament?.matches || [];
  const totalRounds = tournament?.round_count || 0;
  if (!totalRounds) {
    return (
      <div data-testid="pv-bracket-empty" className="h-full flex items-center justify-center text-[#6a766e]" style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }}>
        Bracket has not been generated yet.
      </div>
    );
  }

  const slotsInCol = byRound(matches, 1).length || (tournament.bracket_size ? tournament.bracket_size / 2 : 0);

  // ---- 32-slot draw: four quarters + final four ----
  if (slotsInCol === 16 && totalRounds === 5) {
    const r1 = byRound(matches, 1); const r2 = byRound(matches, 2); const r3 = byRound(matches, 3);
    const slates = [0, 1, 2, 3].map((q) => ({
      key: `quarter-${q}`,
      title: 'Bracket',
      subtitle: `Quarter ${q + 1} of 4`,
      rounds: [
        { round: 1, label: 'Round 1', matches: r1.slice(q * 4, q * 4 + 4) },
        { round: 2, label: 'Round 2', matches: r2.slice(q * 2, q * 2 + 2) },
        { round: 3, label: 'Quarterfinal', matches: r3.slice(q, q + 1) },
      ],
      aside: <AdvancesCard match={r3[q]} />,
    }));
    slates.push({
      key: 'final-four',
      title: 'Bracket',
      subtitle: 'Semifinals & Final',
      rounds: [
        { round: 4, label: 'Semifinals', matches: byRound(matches, 4) },
        { round: 5, label: 'Final', matches: byRound(matches, 5) },
      ],
      aside: tournament.champion ? <ChampionCard tournament={tournament} /> : null,
    });
    const g = QUARTER;
    return (
      <SlateRotator
        slates={slates}
        active={active}
        testidPrefix="pv-bracket-slates"
        renderSlate={(s) => (
          <FitToBox naturalWidth={(s.rounds.length + (s.aside ? 1 : 0)) * g.COL_W + (s.rounds.length - 1) * g.GAP_W} maxScale={1.4} testid="pv-bracket">
            <BracketColumns rounds={s.rounds} g={g} aside={s.aside} />
          </FitToBox>
        )}
      />
    );
  }

  // ---- ≤16 slots (and fallback for larger): single screen ----
  const rounds = [];
  for (let r = 1; r <= totalRounds; r += 1) rounds.push({ round: r, label: roundLabel(r, totalRounds), matches: byRound(matches, r) });
  const g = FULL;
  return (
    <FitToBox naturalWidth={totalRounds * g.COL_W + (totalRounds - 1) * g.GAP_W} maxScale={slotsInCol <= 4 ? 1.5 : 1.25} testid="pv-bracket">
      <BracketColumns rounds={rounds} g={g} footer={<ChampionCard tournament={tournament} />} />
    </FitToBox>
  );
}
